<template>
    <div class="order-process__configure-3d-model" v-if="current3DFile">
        <transition name="fade" mode="out-in">
            <div
                class="configure-3d-model__analysis-tool"
                v-if="
                    [viewports.MOBILE.name, viewports.TABLET.name].includes(
                        currentViewport
                    )
                "
            >
                <component :is="currentAnalysisTool"></component>
                <b-modal
                    v-model="repairModelIsOpen"
                    transition="expand"
                    full-screen
                    :can-cancel="false"
                >
                    <repair-analysis-info></repair-analysis-info>
                </b-modal>
            </div>
        </transition>
        <div class="configuration-steps">
            <div class="configuration-steps__header">
                <transition name="fade" mode="out-in">
                    <div
                        v-if="currentConfigure3DStep === 0"
                        key="header-step-basics"
                    >
                        <p class="is-size-5 has-text-grey">
                            {{
                                $t('checkout.configure.inputLabels.objectName')
                            }}
                        </p>
                    </div>
                    <div
                        v-if="currentConfigure3DStep === 1"
                        key="header-step-materials"
                    >
                        <p class="is-size-4 has-text-black">
                            <strong>{{ current3DFile.name }}</strong>
                        </p>
                        <p class="is-size-5 has-text-grey">
                            {{
                                $t(
                                    'checkout.configure.inputLabels.materialAndColor'
                                )
                            }}
                        </p>
                    </div>
                    <div
                        v-if="currentConfigure3DStep === 2"
                        key="header-step-finishes"
                    >
                        <div
                            class="is-size-4 has-text-black is-flex is-align-items-center"
                        >
                            <b-button
                                v-if="currentConfigure3DStep === 2"
                                @click="
                                    slideTransition = 'slide-right';
                                    currentConfigure3DStep = 1;
                                "
                                type="is-primary-dark is-ghost"
                                size="is-medium"
                                class="pl-0"
                                icon-left="arrow-left"
                                :disabled="goingToCartIsPending"
                            >
                                {{ $t('checkout.cart.actionLabels.goBack') }}
                            </b-button>
                            <strong
                                style="
                                    overflow: hidden;
                                    white-space: nowrap;
                                    text-overflow: ellipsis;
                                    word-break: keep-all;
                                "
                                >{{ current3DFile.name }}</strong
                            >
                        </div>
                        <div class="materials__select">
                            <div class="select__material-option">
                                <PrintModeOption
                                    :update-pricing="false"
                                    :is-selected="false"
                                    :is-loading="false"
                                    :print-mode="
                                        printModeById(selectedPrintMode)
                                    "
                                    :selectable="false"
                                ></PrintModeOption>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>
            <div class="configuration-steps__content p-3">
                <transition :name="slideTransition" mode="out-in">
                    <div
                        v-if="currentConfigure3DStep === 0"
                        key="step-basics"
                        class="p-2"
                    >
                        <template v-if="currentCartItem.objectId">
                            <configure-file-name
                                :print-file-id="currentCartItem.objectId"
                            ></configure-file-name>
                            <configure-length-unit
                                :print-file-id="currentCartItem.objectId"
                            ></configure-length-unit>
                        </template>
                        <div
                            v-if="current3DFile.repairInfo"
                            class="repair-agreement has-text-grey-dark py-3"
                        >
                            <b-checkbox v-model="acceptRepairAgreement">
                                Ich habe die
                                <a
                                    href="#"
                                    @click.stop="
                                        openAnalysisTool('repairAnalysisInfo')
                                    "
                                    class="link has-text-primary"
                                    >durchgeführten Reparaturen</a
                                >
                                eingesehen und erkläre mich damit einverstanden.
                            </b-checkbox>
                        </div>
                    </div>
                    <div
                        v-if="currentConfigure3DStep === 1"
                        class="configure-3d-model__materials"
                        key="step-materials"
                    >
                        <PreviewTabSwitch
                            v-if="
                                [
                                    viewports.MOBILE.name,
                                    viewports.TABLET.name,
                                ].includes(currentViewport)
                            "
                        ></PreviewTabSwitch>
                        <Configure3DModelScaleSlider
                            :class="'has-background-white'"
                            v-if="screenSize.width < viewports.DESKTOP.minWidth"
                        ></Configure3DModelScaleSlider>
                        <div
                            v-if="screenSize.width < viewports.DESKTOP.minWidth"
                            class="configure-3d-model__measurements"
                        >
                            <br />
                            <p class="is-size-5 has-text-grey">
                                {{
                                    $t('checkout.configure.objectMeasurements')
                                }}:
                            </p>
                            <br />
                            <Configure3DModelScaleUnits></Configure3DModelScaleUnits>
                        </div>
                        <br />
                        <PrintModeSelect></PrintModeSelect>
                        <PrintQualityInfo
                            v-if="screenSize.width < viewports.DESKTOP.minWidth"
                        ></PrintQualityInfo>
                    </div>
                    <div
                        v-if="currentConfigure3DStep === 2"
                        class="configure-3d-model__finishes"
                        key="step-finishes"
                    >
                        <PrintModeChooseFinishes />
                    </div>
                </transition>
            </div>

            <div class="configuration-steps__footer">
                <template v-if="currentConfigure3DStep === 0">
                    <div class="has-text-centered">
                        <b-button
                            type="is-primary-dark is-outlined"
                            class="px-6"
                            :disabled="
                                current3DFile.repairInfo &&
                                !acceptRepairAgreement
                            "
                            @click="setGivenUnitOfLengths()"
                            >{{ $t('checkout.nextStep') }}
                        </b-button>
                    </div>
                </template>
                <template v-if="currentConfigure3DStep >= 1">
                    <div>
                        <label
                            class="has-text-primary-dark"
                            :for="'cart-item-quantity-' + current3DFile.id"
                            >{{
                                $t('checkout.configure.inputLabels.quantity')
                            }}</label
                        >
                        <NumberInput
                            v-model="cartItemQuantity"
                            :minimum-value="1"
                            :id="'cart-item-quantity-' + current3DFile._id"
                        ></NumberInput>
                    </div>
                    <div
                        class="is-flex-tablet is-justify-content-space-between is-align-items-center pt-3"
                    >
                        <SelectedPrice class="is-flex-grow-2" />
                        <template v-if="currentConfigure3DStep === 1">
                            <b-button
                                native-class="px-6"
                                type="is-primary-dark"
                                @click="
                                    slideTransition = 'slide-left';
                                    setPrintMode();
                                "
                                :disabled="
                                    !printQualityInfo.printabilityForSelected
                                        .printable || goingToCartIsPending
                                "
                                :loading="goingToCartIsPending"
                                :expanded="
                                    [viewports.MOBILE.name].includes(
                                        currentViewport
                                    )
                                "
                            >
                                {{
                                    $t(
                                        'checkout.cart.actionLabels.chooseFinishes'
                                    )
                                }}
                            </b-button>
                        </template>
                        <template v-if="currentConfigure3DStep === 2">
                            <p class="control">
                                <b-button
                                    type="is-primary-dark"
                                    @click="
                                        slideTransition = 'slide-left';
                                        goToCart();
                                    "
                                    :disabled="
                                        !printQualityInfo
                                            .printabilityForSelected
                                            .printable ||
                                        goingToCartIsPending ||
                                        (current3DFile.repairInfo &&
                                            !acceptRepairAgreement)
                                    "
                                    icon-right="cart"
                                    :loading="goingToCartIsPending"
                                    :expanded="
                                        [viewports.MOBILE.name].includes(
                                            currentViewport
                                        )
                                    "
                                    class="px-5"
                                >
                                    {{
                                        $t(
                                            'checkout.cart.actionLabels.addToCart'
                                        )
                                    }}
                                </b-button>
                            </p>
                        </template>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import Canvas3D from './Canvas3D';
import PrintQualityInfo from '../shared/PrintQualityInfo';
import PrintModeSelect from './printmode-select/PrintModeSelect';
import { mapGetters } from 'vuex';
import { viewports } from '../../store/constants/viewports';
import Configure3DModelScaleSlider from './Configure3DModelScaleSlider';
import Configure3DModelScaleUnits from './Configure3DModelScaleUnits';
import { eventBus } from '../../events/event-bus';
import PreviewTabSwitch from './PreviewTabSwitch';
import HistogramPictures from './3d-dashboard/HistogramPictures';
import RepairAnalysisInfo from './3d-dashboard/RepairAnalysisInfo';
import NumberInput from '../shared/NumberInput';
import ModalView from '../shared/ModalView';
import SelectedPrice from './printmode-select/SelectedPrice';
import PrintModeChooseFinishes from './printmode-select/PrintModeChooseFinishes';
import ConfigureLengthUnit from './ItemConfigurations/ConfigureLengthUnit';
import ConfigureFileName from './ItemConfigurations/ConfigureFileName';
import PrintModeOption from './printmode-select/PrintModeOption';
import { orderProcess } from '../../store/constants/order-process';

export default {
    name: 'Configure3DModel',
    components: {
        ConfigureLengthUnit,
        ConfigureFileName,
        PrintModeChooseFinishes,
        NumberInput,
        PreviewTabSwitch,
        PrintModeSelect,
        PrintQualityInfo,
        Canvas3D,
        Configure3DModelScaleSlider,
        Configure3DModelScaleUnits,
        HistogramPictures,
        RepairAnalysisInfo,
        ModalView,
        SelectedPrice,
        PrintModeOption,
    },
    data() {
        return {
            currentStep: 0,
            viewports: viewports,
            scale: 1,
            goingToCartIsPending: false,
            givenUnitOfLength: '',
            objectName: '',
            currentAnalysisTool: 'Canvas3D',
            repairModelIsOpen: false,
            slideTransition: 'slide-left',
        };
    },
    computed: {
        ...mapGetters([
            'printModes',
            'current3DFile',
            'currentViewport',
            'screenSize',
            'currentCartItem',
            'printQualityInfo',
            'userPreferences',
            'printModeById',
        ]),
        acceptRepairAgreement: {
            get() {
                return this.currentCartItem.acknowledgedRepairInfo || false;
            },
            set(value) {
                this.$store.dispatch('updateCurrentCartItem', {
                    acknowledgedRepairInfo: value,
                });
            },
        },

        selectedPrintMode() {
            if (this.currentCartItem && this.currentCartItem.printModeId) {
                return this.currentCartItem.printModeId;
            }
            return null;
        },

        cartItemQuantity: {
            get() {
                if (this.currentCartItem) {
                    return this.currentCartItem.quantity;
                }
                return null;
            },
            set(quantity) {
                this.$store.dispatch('updateCurrentCartItem', {
                    quantity: quantity,
                });
            },
        },
        estimatedCartItemPrice() {},
        objectScale: {
            get() {
                return this.current3DFile.scale;
            },
            set(value) {
                this.$store.dispatch('updateCurrentCartItem', { scale: value });
            },
        },
        currentConfigure3DStep: {
            get() {
                return this.$store.getters['currentConfigure3DStep'];
            },
            set(configure3DStep) {
                this.$store.dispatch(
                    'setCurrentConfigure3DStep',
                    configure3DStep
                );
            },
        },
    },
    methods: {
        async goToCart() {
            this.goingToCartIsPending = true;

            window.scrollTo({ top: 0, behavior: 'smooth' });

            /** */

            try {
                eventBus.openAnalysisTool('UploadNewItemDropArea');
                eventBus.triggeredAddItemToCart();

                await this.$store.dispatch(
                    'setCurrentOrderProcessStep',
                    orderProcess.STEPS.VIEW_CART
                );
                await this.$store.dispatch('moveCurrentCartItemToCart');
            } catch (error) {
                console.log(error);

                eventBus.notification({
                    type: 'warning',
                    header: 'Hier scheint ein Fehler aufgetreten zu sein.',
                    body: 'Das Objekt konnte nicht in den Warenkorb gelegt werden.',
                });
            }
            this.goingToCartIsPending = false;
        },
        setGivenUnitOfLengths() {
            try {
                this.currentConfigure3DStep = 1;
                this.$store.dispatch('createCartItem');
                this.$store.dispatch(
                    'persist3DFile',
                    this.currentCartItem.objectId
                );
            } catch {
                eventBus.notification({
                    type: 'warning',
                    header: 'Hier scheint ein Fehler aufgetreten zu sein.',
                    body: 'Das Objekt konnte nicht verarbeitet werden. Bitte versuchen Sie es zu einem späteren Zeitpunkt erneut.',
                });
            }
        },
        async setPrintMode() {
            this.currentConfigure3DStep = 2;
        },
        openAnalysisTool(tool) {
            eventBus.openAnalysisTool(tool);
        },
    },
    created() {
        this.$store.dispatch('fetchPrintModes');
        if (this.current3DFile.repairInfo) {
            this.repairModelIsOpen = true;
        }
        eventBus.$on('openAnalysisTool', (tool) => {
            if (tool === 'repairAnalysisInfo') {
                this.repairModelIsOpen = true;
            } else {
                this.currentAnalysisTool = tool;
            }
        });
    },

    mounted() {
        this.objectName = this.current3DFile.name;
        this.givenUnitOfLength = this.current3DFile.givenUnitOfLength;
    },
    beforeDestroy() {
        this.currentConfigure3DStep = 0;
    },
};
</script>

<style lang="scss">
.configuration-steps {
    display: flex;
    max-height: 800px;
    height: 100%;
    flex-direction: column;
    justify-content: space-between;
    overflow: hidden;

    .configuration-steps__header,
    .configuration-steps__footer {
        padding: 1rem;
    }

    .configuration-steps__header {
        border-bottom: 2px solid $primary;
    }

    .configuration-steps__content {
        flex-grow: 1;
        overflow-y: auto;

        @media screen and (min-width: $tablet) {
            height: 0;
        }
    }

    .configuration-steps__footer {
        box-shadow: 6px 8px 19px 3px transparentize($grey-darker, 0.7);
        position: relative;
    }
}
</style>
